import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Mail,
  FileText,
  Users,
  BarChart3,
  Settings,
  Plus,
  Search,
  Clock,
  ArrowRight,
} from "lucide-react";
import { useCommandPaletteStore } from "@/stores";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";

interface CommandItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcut?: string;
  action: () => void;
  keywords?: string[];
}

interface CommandGroup {
  heading: string;
  items: CommandItem[];
}

export function CommandPalette() {
  const { isOpen, close } = useCommandPaletteStore();
  const navigate = useNavigate();
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("admin-recent-searches");
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  const addRecentSearch = useCallback((search: string) => {
    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s !== search);
      const updated = [search, ...filtered].slice(0, 5);
      localStorage.setItem("admin-recent-searches", JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Keyboard shortcut to open (Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleNavigate = (path: string, label: string) => {
    addRecentSearch(label);
    navigate(path);
    close();
  };

  const navigationItems: CommandGroup = {
    heading: "Navigation",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        action: () => handleNavigate("/", "Dashboard"),
        keywords: ["home", "overview", "main"],
      },
      {
        id: "newsletters",
        label: "Newsletters",
        icon: Mail,
        action: () => handleNavigate("/newsletters", "Newsletters"),
        keywords: ["email", "campaigns", "marketing"],
      },
      {
        id: "content",
        label: "Content",
        icon: FileText,
        action: () => handleNavigate("/content", "Content"),
        keywords: ["posts", "articles", "blog"],
      },
      {
        id: "users",
        label: "Users",
        icon: Users,
        action: () => handleNavigate("/users", "Users"),
        keywords: ["subscribers", "members", "customers"],
      },
      {
        id: "analytics",
        label: "Analytics",
        icon: BarChart3,
        action: () => handleNavigate("/analytics", "Analytics"),
        keywords: ["stats", "metrics", "reports", "data"],
      },
      {
        id: "settings",
        label: "Settings",
        icon: Settings,
        shortcut: "Cmd+,",
        action: () => handleNavigate("/settings", "Settings"),
        keywords: ["preferences", "config", "options"],
      },
    ],
  };

  const quickActions: CommandGroup = {
    heading: "Quick Actions",
    items: [
      {
        id: "new-newsletter",
        label: "Create New Newsletter",
        icon: Plus,
        action: () => handleNavigate("/newsletters/new", "Create New Newsletter"),
        keywords: ["add", "create", "compose"],
      },
      {
        id: "view-users",
        label: "View All Users",
        icon: Users,
        action: () => handleNavigate("/users", "View All Users"),
        keywords: ["list", "subscribers"],
      },
      {
        id: "view-analytics",
        label: "View Analytics Report",
        icon: BarChart3,
        action: () => handleNavigate("/analytics", "View Analytics Report"),
        keywords: ["report", "stats", "metrics"],
      },
    ],
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
            <Search className="h-10 w-10 mb-2 opacity-50" />
            <p>No results found</p>
            <p className="text-xs mt-1">Try searching for something else</p>
          </div>
        </CommandEmpty>

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentSearches.map((search) => (
                <CommandItem
                  key={search}
                  onSelect={() => {
                    // Find matching navigation item
                    const allItems = [
                      ...navigationItems.items,
                      ...quickActions.items,
                    ];
                    const match = allItems.find(
                      (item) => item.label.toLowerCase() === search.toLowerCase()
                    );
                    if (match) {
                      match.action();
                    }
                  }}
                >
                  <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{search}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Navigation */}
        <CommandGroup heading={navigationItems.heading}>
          {navigationItems.items.map((item) => (
            <CommandItem
              key={item.id}
              onSelect={item.action}
              className="flex items-center"
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
              {item.shortcut && (
                <CommandShortcut>{item.shortcut}</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading={quickActions.heading}>
          {quickActions.items.map((item) => (
            <CommandItem
              key={item.id}
              onSelect={item.action}
              className="flex items-center"
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
              <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
